import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarRange, FileBarChart2, Send, Loader2, Receipt, Building2, ArrowUpRight, ArrowDownRight, Share2,
} from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MonthFieldPT from "@/components/MonthFieldPT";

const METHOD_LABEL = {
  pix: "PIX",
  transferencia: "Transferência",
  dinheiro: "Dinheiro",
  cheque: "Cheque",
  outro: "Outro",
};

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
};

const digitsOnly = (s) => (s || "").replace(/\D+/g, "");

/**
 * SocioMonthlyRepasse — monthly report for a single socio.
 * Shows how much came in (NFs → company net) and how much the socio actually
 * received. Includes a WhatsApp button that sends a simple text summary.
 */
export default function SocioMonthlyRepasse({ socio }) {
  const [month, setMonth] = useState(() => {
    // Default to current month (last day = usual send date)
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!socio?.id || !month) return;
    setLoading(true);
    try {
      const r = await api.get(`/finance/socios/${socio.id}/monthly-report`, {
        params: { month },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Erro ao carregar relatório mensal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [socio?.id, month]);

  const whatsappText = useMemo(() => {
    if (!data) return "";
    const nome = data.socio?.name || "sócio";
    const mes = monthLabel(month);
    const paid = BRL(data.total_paid_month);
    const earned = BRL(data.total_earned_month);
    const nfCount = data.nf_count_month || 0;
    const net = BRL(data.company_net_month);
    const lines = [];
    lines.push(`*Relatório de repasse — ${mes}*`);
    lines.push("");
    lines.push(`Olá ${nome}! Segue o resumo do mês:`);
    lines.push("");
    lines.push(`💰 *Pago a você:* ${paid}`);
    lines.push(`📊 *Devido no mês:* ${earned} (de ${nfCount} ${nfCount === 1 ? "orçamento finalizado" : "orçamentos finalizados"})`);
    lines.push(`🏢 *Líquido da empresa no mês:* ${net}`);
    if (data.payments?.length) {
      lines.push("");
      lines.push("*Pagamentos realizados:*");
      for (const p of data.payments) {
        const metodo = METHOD_LABEL[p.method] || p.method || "—";
        lines.push(`• ${formatDateBR(p.payment_date)} — ${BRL(p.amount)} (${metodo})`);
      }
    }
    const balance = (data.total_earned_month || 0) - (data.total_paid_month || 0);
    lines.push("");
    if (balance <= 0.009) {
      lines.push(`✅ *Saldo do mês: em dia*`);
    } else {
      lines.push(`⚠️ *Saldo pendente do mês:* ${BRL(balance)}`);
    }
    lines.push("");
    lines.push("_AMC Escolas — gestão financeira_");
    return lines.join("\n");
  }, [data, month]);

  const sendWhatsApp = () => {
    if (!data) return;
    const phone = digitsOnly(socio?.phone || "");
    const base = phone
      ? `https://wa.me/${phone.startsWith("55") ? phone : "55" + phone}`
      : `https://wa.me/`;
    const url = `${base}?text=${encodeURIComponent(whatsappText)}`;
    window.open(url, "_blank", "noopener");
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(whatsappText);
      toast.success("Relatório copiado — cole no WhatsApp");
    } catch {
      toast.error("Não consegui copiar — selecione e copie manualmente");
    }
  };

  const hasPhone = !!digitsOnly(socio?.phone || "");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="socio-monthly-repasse">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
          <FileBarChart2 className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900 leading-tight">
            Repasse do mês
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Resumo visual + mensagem pronta pra enviar ao sócio no WhatsApp.
          </p>
        </div>
      </div>

      {/* Month selector */}
      <div className="min-w-0 mb-4">
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Mês</label>
        <MonthFieldPT value={month} onChange={setMonth} testid="repasse-month" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando...
        </div>
      ) : data ? (
        <>
          {/* Headline cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                Pago no mês
              </p>
              <p className="font-black text-lg sm:text-xl text-emerald-700 mt-1 tabular-nums">
                {BRL(data.total_paid_month)}
              </p>
              <p className="text-[10px] text-emerald-700/80 mt-0.5">
                {data.payments.length} {data.payments.length === 1 ? "pagamento" : "pagamentos"}
              </p>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Devido no mês
              </p>
              <p className="font-black text-lg sm:text-xl text-slate-900 mt-1 tabular-nums">
                {BRL(data.total_earned_month)}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {data.nf_count_month} {data.nf_count_month === 1 ? "orçamento" : "orçamentos"} finalizados
              </p>
            </div>
            <div className="rounded-xl bg-indigo-50 border border-indigo-200 p-3">
              <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">
                Líquido empresa
              </p>
              <p className="font-black text-lg sm:text-xl text-indigo-700 mt-1 tabular-nums">
                {BRL(data.company_net_month)}
              </p>
              <p className="text-[10px] text-indigo-700/80 mt-0.5">no mês</p>
            </div>
          </div>

          {/* NFs origin list */}
          <div className="mt-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              De onde veio ({data.nf_rows.length} NF)
            </h3>
            {data.nf_rows.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
                Nenhuma NF finalizada neste mês.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {data.nf_rows.map((n, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 p-2.5 rounded-lg border border-emerald-100 bg-emerald-50/40"
                  >
                    <ArrowUpRight className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-semibold text-slate-800 text-sm">
                          NF nº {n.nf_number || "—"} · Orç. #{n.budget_number}
                        </p>
                        <p className="text-sm font-bold text-emerald-700 tabular-nums whitespace-nowrap">
                          +{BRL(n.value)}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {n.school_name}
                        {n.tier ? ` · Faixa ${n.tier} · ${n.pct}%` : ""}
                      </p>
                      <p className="text-[10px] text-slate-400">{formatDateBR(n.nf_date)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Payments in this month */}
          <div className="mt-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Pagamentos no mês ({data.payments.length})
            </h3>
            {data.payments.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
                <Receipt className="w-5 h-5 mx-auto text-slate-300 mb-1" />
                Nenhum pagamento feito neste mês ainda.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {data.payments.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-100 bg-amber-50/40"
                  >
                    <ArrowDownRight className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-semibold text-slate-800 text-sm">
                          {METHOD_LABEL[p.method] || p.method}
                        </p>
                        <p className="text-sm font-bold text-amber-700 tabular-nums whitespace-nowrap">
                          −{BRL(p.amount)}
                        </p>
                      </div>
                      <p className="text-[10px] text-slate-500">
                        {formatDateBR(p.payment_date)}
                        {p.note ? ` · ${p.note}` : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Send actions */}
          <div className="mt-5 pt-4 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={sendWhatsApp}
              className="w-full sm:flex-1 h-10 bg-green-600 hover:bg-green-700 text-white"
              data-testid="btn-send-wa"
            >
              <Send className="w-4 h-4 mr-1.5" />
              Enviar ao sócio no WhatsApp
            </Button>
            <Button
              variant="outline"
              onClick={copyText}
              className="w-full sm:w-auto h-10"
              data-testid="btn-copy-wa"
            >
              <Share2 className="w-4 h-4 mr-1.5" />
              Copiar texto
            </Button>
          </div>
          {!hasPhone && (
            <p className="text-[11px] text-amber-700 mt-2">
              💡 Dica: cadastre o celular do sócio em{" "}
              <strong>Configurações financeiras</strong> para abrir o WhatsApp já com o contato preenchido.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}
